import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink } from 'react-router-dom'
import { workbenchApi } from '../api/workbench'
import { outboxApi } from '../api/outbox'
import { useSpoke } from '../hooks/useSpoke'
import { toast } from '../lib/toasts'
import { sessionKey, useWorking } from '../lib/working'
import type { CraftingSession } from '../types/workbench'
import { groupSessions } from '../lib/sessions'

// THE SUB-ITEMS UNDER CRAFTING (owner, 2026-10-07): each opportunity being crafted, a spinner next to the name while Scout is
// working on it, a dot when an answer is waiting to be seen, and a toast when a reminder is raised because an answer
// has waited unseen for the deployment's hour. Read every half minute; the chat's own replies refresh it at once.

export const SESSIONS_KEY = 'workbench-sessions'
const SHOWN = 'scout.reminded-shown'

function shown(): Record<string, string> { try { return JSON.parse(localStorage.getItem(SHOWN) ?? '{}') as Record<string, string> } catch { return {} } }
function markShown(map: Record<string, string>) { try { localStorage.setItem(SHOWN, JSON.stringify(map)) } catch { /* a private window: the toast shows again next time */ } }

export function labelOf(s: CraftingSession): string { return s.personName ?? s.companyName }

export function CraftingSessionsNav() {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: [SESSIONS_KEY, spoke], queryFn: () => workbenchApi.sessions(spoke), refetchInterval: 30_000, staleTime: 10_000 })
  const working = useWorking()
  const sessions = q.data ?? []
  // THE MAIL LEG NEEDS THE REP'S OWN GRANT (stand-up 2026-10-14: Don waited for the hour's email with no grant on file, and nothing
  // said so): when they have sessions and no send grant, the reminder says where the permission is given, once
  const grant = useQuery({ queryKey: ['outbox-grant', spoke], queryFn: () => outboxApi.grant(spoke), enabled: sessions.length > 0, staleTime: 300_000 })
  const noGrant = grant.data?.configured === true && grant.data.granted === false
  const grantWords = 'Reminders by mail need your mailbox’s permission, once: Outbox, then “Send from my mailbox”.'

  // a reminder raised since the last one this browser showed: one toast, once
  useEffect(() => {
    if (typeof localStorage === 'undefined') return
    const seen = shown()
    let changed = false
    for (const s of sessions) {
      const key = sessionKey(s.companyId, s.personId, s.engagementId)
      if (!s.remindedAt || seen[key] === s.remindedAt) continue
      seen[key] = s.remindedAt; changed = true
      toast({ title: `${labelOf(s)} is waiting for you`, body: `Scout answered and you have not been back to it for an hour.${noGrant ? ` ${grantWords}` : ''}`, href: s.route, kind: 'reminder' }, 0)
    }
    if (changed) markShown(seen)
  }, [sessions, noGrant])

  if (sessions.length === 0) return null
  // ONE ENTRY PER PERSON (TFY-33): a person spoken with on two opportunities is one name with each conversation named under it,
  // never two entries with one name. Working comes from the server as well as from this tab, so a spinner shows anywhere.
  const groups = groupSessions(sessions)
  const mark = (s: CraftingSession) => {
    const busy = working.has(sessionKey(s.companyId, s.personId, s.engagementId)) || s.working
    return busy ? <i className="nav-spin" aria-label="Scout is working on it" /> : s.waiting ? <i className="nav-dot" aria-label="An answer is waiting" /> : <i className="nav-gap" />
  }
  return (
    <div className="nav-subs" aria-label="What you are crafting">
      {noGrant && sessions.some((s) => s.waiting) && (
        <NavLink to="/outbox" className="nav-sub nav-nudge" title={grantWords}><i className="nav-gap" /><span className="sub-text"><span className="sub-opp">Mail reminders need your mailbox’s permission, once</span></span></NavLink>
      )}
      {groups.map((g) => {
        const head = g.sessions[0]
        const key = sessionKey(head.companyId, head.personId, null) + '|group'
        if (g.sessions.length === 1) {
          const s = head
          return (
            <NavLink key={key} to={s.route} className={({ isActive }) => `nav-sub ${isActive ? 'active' : ''}`} title={s.subject ?? s.companyName}>
              {mark(s)}
              <span className="sub-text">
                <span className="sub-name">{labelOf(s)}{s.personName && <span className="sub-account"> · {s.companyName}</span>}</span>
                {s.subject && <span className="sub-opp">{s.subject}</span>}
              </span>
            </NavLink>
          )
        }
        return (
          <div key={key} className="nav-sub-group">
            <span className="nav-sub static">
              {g.sessions.some((s) => working.has(sessionKey(s.companyId, s.personId, s.engagementId)) || s.working) ? <i className="nav-spin" aria-label="Scout is working on it" /> : g.sessions.some((s) => s.waiting) ? <i className="nav-dot" aria-label="An answer is waiting" /> : <i className="nav-gap" />}
              <span className="sub-name">{labelOf(head)}{head.personName && <span className="sub-account"> · {head.companyName}</span>}</span>
            </span>
            {g.sessions.map((s) => {
              const k = sessionKey(s.companyId, s.personId, s.engagementId)
              return (
                <NavLink key={k} to={s.route} className={({ isActive }) => `nav-sub nav-sub-opp ${isActive ? 'active' : ''}`} title={s.subject ?? 'No opportunity chosen'}>
                  {mark(s)}
                  <span className="sub-opp">{s.subject ?? 'No opportunity chosen'}</span>
                </NavLink>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
